import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { CreateVenue, UpdateVenue } from "~pages/Venues/types/upsertVenue.ts";
import { useExportVenues } from "~pages/Venues/VenueListPage/hooks/useExportVenues.ts";
import { useImportVenues } from "~pages/Venues/VenueListPage/hooks/useImportVenues.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { ensureDefaultOperationResponse, isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateVenue } from "./useCreateVenue.ts";
import { useDeleteVenue } from "./useDeleteVenue.ts";
import { useUpdateVenue } from "./useUpdateVenue.ts";

export const useVenueService = () => {
  const { mutateAsync: createVenueFn, isPending: isCreatePending } = useCreateVenue();
  const { mutateAsync: updateVenueFn, isPending: isUpdatePending } = useUpdateVenue();
  const { mutateAsync: deleteVenueFn, isPending: isDeletePending } = useDeleteVenue();
  const { mutateAsync: importVenuesFn, isPending: isImportPending } = useImportVenues();
  const { mutateAsync: exportVenuesFn, isPending: isExportPending } = useExportVenues();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove, execute } = useApiOperations(ApiOperationItems.VENUE);

  const createVenue = async (payload: CreateVenue, options?: { redirectToList?: boolean }) => {
    const createdVenue = await create(() => createVenueFn(payload), {
      successMessageId: "venue.create.success",
    });

    if (createdVenue && options?.redirectToList) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/venues`, { state: { search: payload.nameLocal } });
    }

    return createdVenue;
  };

  const updateVenue = (venueId: string, payload: Omit<UpdateVenue, "venueId">) =>
    update(() => updateVenueFn({ ...payload, venueId }), {
      successMessageId: "venue.update.success",
    });

  const deleteVenue = async (venueId: string) =>
    Boolean(await remove(() => deleteVenueFn(venueId), { successMessageId: "venue.delete.success" }));

  const importVenues = async (fileName: string) =>
    ensureDefaultOperationResponse(
      await execute(() => importVenuesFn(fileName), {
        successMessageId: "venues.import.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Venues import was not started",
    );

  const exportVenues = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    ensureDefaultOperationResponse(
      await execute(() => exportVenuesFn(fieldNames), {
        successMessageId: "venues.export.started",
        isSucceeded: isDefaultOperationSucceeded,
      }),
      "Venues export was not started",
    );

  return {
    createVenue,
    isCreatePending,
    updateVenue,
    isUpdatePending,
    deleteVenue,
    isDeletePending,
    importVenues,
    isImportPending,
    exportVenues,
    isExportPending,
  };
};
